import toast from "solid-toast";

export type RecordingError = {
  type: "media" | "storage" | "database" | "network";
  message: string;
  retryable: boolean;
  timestamp: Date;
  context?: any;
};

export class RecordingErrorHandler {
  private static readonly RECOVERY_KEY = "recording_recovery_data";
  private static readonly MAX_RETRIES = 3;
  private retryCount: number = 0;

  // Save recording state for recovery
  static saveRecoveryData(
    sessionId: string,
    chunks: Blob[],
    startTime: number,
    metadata: any
  ) {
    try {
      const recoveryData = {
        sessionId,
        startTime,
        metadata,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(this.RECOVERY_KEY, JSON.stringify(recoveryData));

      // Store audio chunks in IndexedDB for larger data
      const request = indexedDB.open("recording_recovery", 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("chunks")) {
          db.createObjectStore("chunks", { keyPath: "sessionId" });
        }
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const tx = db.transaction("chunks", "readwrite");
        const store = tx.objectStore("chunks");

        const blob = new Blob(chunks, { type: "audio/webm" });
        store.put({ sessionId, blob });
      };
    } catch (error) {
      console.error("Failed to save recovery data:", error);
    }
  }

  // Check for recoverable recording
  static async checkForRecovery(): Promise<{
    sessionId: string;
    blob: Blob;
    startTime: number;
    metadata: any;
  } | null> {
    try {
      const recoveryData = localStorage.getItem(this.RECOVERY_KEY);
      if (!recoveryData) return null;

      const { sessionId, startTime, metadata } = JSON.parse(recoveryData);

      // Get audio chunks from IndexedDB
      return new Promise((resolve) => {
        const request = indexedDB.open("recording_recovery", 1);

        request.onsuccess = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          const tx = db.transaction("chunks", "readonly");
          const store = tx.objectStore("chunks");

          const getRequest = store.get(sessionId);
          getRequest.onsuccess = () => {
            if (getRequest.result) {
              resolve({
                sessionId,
                blob: getRequest.result.blob,
                startTime,
                metadata,
              });
            } else {
              resolve(null);
            }
          };
          getRequest.onerror = () => resolve(null);
        };

        request.onerror = () => resolve(null);
      });
    } catch (error) {
      console.error("Failed to check recovery data:", error);
      return null;
    }
  }

  // Clear recovery data after successful save
  static clearRecoveryData() {
    try {
      localStorage.removeItem(this.RECOVERY_KEY);

      const request = indexedDB.open("recording_recovery", 1);
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const tx = db.transaction("chunks", "readwrite");
        const store = tx.objectStore("chunks");
        store.clear();
      };
    } catch (error) {
      console.error("Failed to clear recovery data:", error);
    }
  }

  // Handle specific error types
  async handleError(error: RecordingError): Promise<boolean> {
    console.error(`Recording error (${error.type}):`, error);

    switch (error.type) {
      case "media":
        toast.error(
          "Microphone access error. Please check your microphone permissions."
        );
        return false; // Not retryable, needs user action

      case "storage":
        if (
          error.retryable &&
          this.retryCount < RecordingErrorHandler.MAX_RETRIES
        ) {
          this.retryCount++;
          toast.error(
            `Failed to save recording. Retrying... (${this.retryCount}/${RecordingErrorHandler.MAX_RETRIES})`
          );
          return true; // Can retry
        }
        toast.error("Failed to save recording. Please try again later.");
        return false;

      case "database":
        if (
          error.retryable &&
          this.retryCount < RecordingErrorHandler.MAX_RETRIES
        ) {
          this.retryCount++;
          toast.error(
            `Database error. Retrying... (${this.retryCount}/${RecordingErrorHandler.MAX_RETRIES})`
          );
          return true; // Can retry
        }
        toast.error(
          "Failed to update recording status. Your recording is saved but may need manual sync."
        );
        return false;

      case "network":
        toast.error("Network error. Please check your connection.");
        return error.retryable; // Might be retryable depending on context

      default:
        toast.error("An unexpected error occurred.");
        return false;
    }
  }

  // Reset retry count
  resetRetryCount() {
    this.retryCount = 0;
  }
}
